import React from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Card } from '@amzn/stencil-react-components/card'
import { Chip, ChipBackgroundColor, ChipSize } from '@amzn/stencil-react-components/chip'
import { Col, Row, Spacer, View } from '@amzn/stencil-react-components/layout'
import { H3, Text } from '@amzn/stencil-react-components/text'

import { PlaceholdersByKind } from 'src/services/import-content/TemplateSpreadsheetService'

export interface FormTemplateImportCardProps {
    actionsDisabled: boolean
    placeholders: PlaceholdersByKind
    onExportSpreadsheet: () => void
    onStartImport: () => void
}

const PlaceholderGroup = ({ label, names }: { label: string; names: string[] }) => (
    <Col
        gridGap='S100'
        dataTestId={`form-template-${label.toLowerCase().replace(/\s+/g, '-')}-placeholders`}
    >
        <Text fontSize='T100' color='neutral70'>
            {label} ({names.length})
        </Text>
        {names.length === 0 ? (
            <Text fontSize='T100' color='neutral70'>
                None
            </Text>
        ) : (
            <View style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {names.map((name) => (
                    <View
                        key={name}
                        backgroundColor='neutral05'
                        borderRadius={16}
                        padding={{ top: 'S100', bottom: 'S100', left: 'S300', right: 'S300' }}
                    >
                        <Text fontSize='T100' color='neutral90'>
                            {name}
                        </Text>
                    </View>
                ))}
            </View>
        )}
    </Col>
)

export function FormTemplateImportCard({
    actionsDisabled,
    placeholders,
    onExportSpreadsheet,
    onStartImport,
}: FormTemplateImportCardProps) {
    return (
        <Card dataTestId='form-template-import-card'>
            <View>
                <Chip backgroundColor={ChipBackgroundColor.Green} size={ChipSize.ExtraSmall}>
                    New
                </Chip>
                <Spacer height='S200' />
                <H3 fontSize='T400'>
                    FormTemplate allows you to import content from a spreadsheet
                </H3>
                <ul data-test-id='form-template-import-description'>
                    <li>
                        <Text fontSize='T100' color='neutral70'>
                            Each {'{{placeholder}}'} in this module becomes a spreadsheet column.
                            Export the spreadsheet to get one with the right columns.
                        </Text>
                    </li>
                    <li>
                        <Text fontSize='T100' color='neutral70'>
                            Every row fills in the placeholders to create its own pages and items,
                            grouped into Item Container modules by the __container__ column, whose
                            value names each module (up to 50 items each).
                        </Text>
                    </li>
                    <li>
                        <Text fontSize='T100' color='neutral70'>
                            This module is never changed and stays as your template, so you can
                            import again to update the generated modules.
                        </Text>
                    </li>
                </ul>
                <Spacer height='S400' />
                <Col gridGap='S300'>
                    <PlaceholderGroup label='Content' names={placeholders.content} />
                    <PlaceholderGroup label='Scoring' names={placeholders.scoring} />
                    <PlaceholderGroup label='Page label' names={placeholders.pageLabels} />
                </Col>
                <Spacer height='S500' />
                <Row alignItems='center' gridGap='S300'>
                    <Button
                        variant={ButtonVariant.Secondary}
                        aria-disabled={actionsDisabled}
                        onClick={onExportSpreadsheet}
                        dataTestId='export-spreadsheet-button'
                    >
                        Export spreadsheet
                    </Button>
                    <Button
                        variant={ButtonVariant.Primary}
                        aria-disabled={actionsDisabled}
                        onClick={onStartImport}
                        dataTestId='start-import'
                    >
                        Start import
                    </Button>
                </Row>
            </View>
        </Card>
    )
}
